/* eslint-disable no-console */
import { APIUserFeatureToggles, FeatureKeys, Features } from '@ardoq/api-types';

export type OverriddenFeatures = Partial<Record<FeatureKeys, boolean>>;

declare global {
  interface Window {
    FEATURE_FLAGS?: APIUserFeatureToggles;
  }
}

export const validateFeatureOverrides = (
  overrides: OverriddenFeatures
): Array<[FeatureKeys, boolean]> => {
  return (Object.entries(overrides) as Array<[FeatureKeys, boolean]>).filter(
    ([name]) => {
      const feature = Features[name];
      if (!feature) {
        warnOnce(`Unknown feature flag "${name}"`);
        return false;
      }
      return true;
    }
  );
};

export const getPersistedOverrides = (
  validFeatures: Array<[FeatureKeys, boolean]>
): APIUserFeatureToggles =>
  validFeatures.reduce<APIUserFeatureToggles>((acc, [name, value]) => {
    const persistedId = Features[name].persistedId;
    return { ...acc, [persistedId]: value };
  }, {});

const SESSION_STORAGE_KEY = 'FEATURE_FLAGS_OVERRIDES';

const loadOverridesFromSessionStorage = (): OverriddenFeatures => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as OverriddenFeatures;
    }
  } catch {
    // Ignore parse errors
  }
  return {};
};

export const saveOverridesToSessionStorage = (
  overrides: OverriddenFeatures
): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // Ignore storage errors
  }
};

const STAGING_HOSTNAME_SUFFIX = '.ardoq.dev';
const CLOUD_PREVIEW_HOSTNAME_SUFFIX = '.ardoq.workers.dev';

export const areLocalOverridesEnabled = (): boolean =>
  Boolean(import.meta.env.DEV) ||
  window.location.hostname.endsWith(STAGING_HOSTNAME_SUFFIX) ||
  window.location.hostname.endsWith(CLOUD_PREVIEW_HOSTNAME_SUFFIX);

/**
 * @returns The feature flag overrides on local dev, staging (*.ardoq.dev) and cloud preview (*.ardoq.workers.dev), otherwise null
 * @see vite.config.ts for how the overrides are injected
 */
export const getLocalFeatureFlagOverrides =
  (): APIUserFeatureToggles | null => {
    if (!areLocalOverridesEnabled()) {
      return null;
    }

    const sessionOverrides = loadOverridesFromSessionStorage();
    const overrides = { ...window.FEATURE_FLAGS, ...sessionOverrides };

    const validFeatures = validateFeatureOverrides(overrides);
    return getPersistedOverrides(validFeatures);
  };

const givenWarnings = new Set<string>();

const warnOnce = (message: string) => {
  if (!givenWarnings.has(message)) {
    console.warn(message);
    givenWarnings.add(message);
  }
};
